import styled from '@emotion/styled';
import React, { FC, useCallback, useEffect, useState } from 'react';
import { Project } from '../../Api';
import { useApiClient } from '../../hooks/useApiClient';
import { Button } from '../shared/Button';
import Require from '../shared/Require';
import theme from '../shared/theme';
import { DemoSlotRow } from './DemoSlotRow';
import { SortableContainer, SortableElement, SortEnd } from 'react-sortable-hoc';
import { CurrentDemoSlotEditor } from './CurrentDemoSlotEditor';

type Props = {
  hackathonId: number;
};

export const DemoSlotEditor: FC<Props> = ({ hackathonId }) => {
  const apiClient = useApiClient();
  const [projects, setProjects] = useState<Project[]>([]);

  useEffect(() => {
    async function loadProjects() {
      const projects = await apiClient.project.getAll(hackathonId);
      if (projects) {
        projects.sort(sortByDemoSlot);
        setProjects(projects);
      }
    }
    loadProjects();
  }, [hackathonId, apiClient, setProjects]);

  const saveDemoProjects = useCallback(
    async (projectIds: number[]) => {
      const updatedProjects = await apiClient.project.setDemoSlots(hackathonId, projectIds);
      if (updatedProjects) {
        updatedProjects.sort(sortByDemoSlot);
        setProjects(updatedProjects);
      }
    },
    [apiClient, hackathonId, setProjects]
  );

  const cancelDemo = useCallback(
    (projectId: number) =>
      saveDemoProjects(
        projects
          .filter(project => project.demoSlot !== undefined)
          .map(project => project.projectId)
          .filter(id => id !== projectId)
      ),
    [projects, saveDemoProjects]
  );

  const addDemo = useCallback(
    (projectId: number) =>
      saveDemoProjects(
        projects
          .filter(project => project.demoSlot !== undefined)
          .map(project => project.projectId)
          .concat(projectId)
      ),
    [projects, saveDemoProjects]
  );

  const clearAll = useCallback(() => saveDemoProjects([]), [saveDemoProjects]);
  const disableClearAll = !projects.filter(p => p.demoSlot !== undefined).length;

  const onSortEnd = useCallback(
    ({ oldIndex, newIndex }: SortEnd) => {
      const newProjects = [...projects];
      newProjects.splice(newIndex, 0, newProjects.splice(oldIndex, 1)[0]);

      saveDemoProjects(newProjects.filter(p => p.demoSlot !== undefined).map(p => p.projectId));
      setProjects(newProjects);
    },
    [projects, saveDemoProjects]
  );

  return (
    <Require required={projects.length}>
      {() => (
        <Card>
          <Header>
            <SectionTitle>Demo Schedule</SectionTitle>
            <Button category="danger" size="sm" onClick={clearAll} disabled={disableClearAll}>
              Reset All
            </Button>
          </Header>
          <CurrentDemoSlotEditor hackathonId={hackathonId} />
          <Divider />
          <SortableTable onSortEnd={onSortEnd} useDragHandle lockAxis="y" helperClass="sorting">
            <tbody>
              {projects!.map((p, index) => (
                <SortableRow index={index} key={p.projectId} project={p} addDemo={addDemo} cancelDemo={cancelDemo} />
              ))}
            </tbody>
          </SortableTable>
        </Card>
      )}
    </Require>
  );
};

const sortByDemoSlot = (a: Project, b: Project) => {
  if (a.demoSlot === null) return b.demoSlot === null ? 0 : 1;

  if (b.demoSlot === null) return -1;

  return a.demoSlot! - b.demoSlot!;
};

const Card = styled.div`
  ${theme.styles.materialPanel};
  background: #fff;
  margin-bottom: 20px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  flex-wrap: wrap;
  gap: 10px;
`;

const SectionTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: ${theme.colors.blue};
`;

const Divider = styled.div`
  height: 1px;
  background: ${theme.colors.borderGrey};
  margin: 20px 0;
`;

const Table = styled.table`
  width: 100%;
  font-size: 13px;
  border-spacing: 0;
  table-layout: fixed;

  & tr:nth-of-type(even) {
    background: #f9f9f9;
  }

  & tr:hover {
    background: ${theme.colors.lightBlue};
  }

  & td {
    padding: 12px 8px;
  }

  @media (max-width: 768px) {
    font-size: 12px;
    
    & td {
      padding: 10px 6px;
    }
  }
`;

const SortableTable = SortableContainer(Table) as any;
const SortableRow = SortableElement(DemoSlotRow) as any;
